import {trans} from '@frontend/helpers'
import {AdsListItem} from '@frontend/modules/public/ads/components/list/AdsListItem'
import clsx from 'clsx'
import {type ReactNode} from 'react'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {Loading} from 'swayok-react-mdb-ui-kit/components/Loading/Loading'
import type {
    AdPreviewDataForList,
    AdsListGridColumnsConfig,
    AdsListProps,
} from '../../AdsModuleTypes'
import {AdsGridItem} from './AdsGridItem'
import {AdsGridPromoBanners} from './AdsGridPromoBanners'
import {AdsListIntersectionObserver} from './AdsListIntersectionObserver'
import {AdsListPromoBanners} from './AdsListPromoBanners'

// Список объявлений для страницы поиска объявлений.
export function AdsList(props: AdsListProps) {

    const {
        listUid,
        ads,
        isAllAdsLoaded,
        isLoadingMoreItems,
        onLoadMore,
        disabled,
        hideIfEmpty,
        presentation = 'grid',
        infiniteScroll = false,
        gridColumns,
        sideMenuVisibleFromBsBreakpoint,
        allowPromoBanners,
        onAdDataChange,
    } = props

    if (!ads || ads.length === 0) {
        return hideIfEmpty
            ? null
            : (
                <div className="py-6 px-5 text-center fs-5">
                    {trans().ads.list.no_ads}
                </div>
            )
    }

    let items: ReactNode
    switch (presentation) {
        case 'list':
            items = (
                <div
                    id="search-results-list"
                    className="mode-list d-grid grid-columns-1 row-gap-5"
                >
                    {ads.map((item, index) => (
                        <AdsListItem
                            key={item.id}
                            gridOrder={(index + 1) * 10}
                            ad={item as AdPreviewDataForList}
                            onAdDataChange={onAdDataChange}
                        />
                    ))}
                    {allowPromoBanners && (
                        <AdsListPromoBanners
                            listUid={listUid}
                            sideMenuVisibleFromBsBreakpoint={sideMenuVisibleFromBsBreakpoint}
                        />
                    )}
                </div>
            )
            break
        default:
            items = (
                <div
                    id="search-results-list"
                    className={clsx(
                        'mode-grid d-grid',
                        'col-gap-3', // для xs и xxs в стилях _ads-list.scss задается 1rem.
                        'row-gap-4 row-gap-sm-5 row-gap-md-6',
                        getGridColumnsClassNames(gridColumns)
                    )}
                >
                    {ads.map((item, index) => (
                        <AdsGridItem
                            key={item.id}
                            gridOrder={(index + 1) * 10}
                            ad={item}
                        />
                    ))}
                    {allowPromoBanners && (
                        <AdsGridPromoBanners
                            listUid={listUid}
                            gridColumns={gridColumns as AdsListGridColumnsConfig}
                            sideMenuVisibleFromBsBreakpoint={sideMenuVisibleFromBsBreakpoint}
                        />
                    )}
                </div>
            )
            break
    }

    const isInfiniteScrollEnabled: boolean = (
        infiniteScroll
        && !disabled
        && !isAllAdsLoaded
        && ads.length > 0
        && ads.length < 100
    )

    const showLoadMoreButton: boolean = (
        !isLoadingMoreItems
        && !isAllAdsLoaded
        && !disabled
        && ads.length > 0
    )

    return (
        <div className="position-relative">
            {items}

            <AdsListIntersectionObserver
                onLoadMore={onLoadMore}
                disabled={!isInfiniteScrollEnabled}
                presentation={presentation}
            />

            <Loading
                loading={isLoadingMoreItems}
                className="mt-4"
            />

            {showLoadMoreButton && (
                <div className="flex-center pt-6">
                    <Button
                        color="gray"
                        onClick={() => onLoadMore(false)}
                        disabled={disabled}
                    >
                        {trans().common.load_more_records}
                    </Button>
                </div>
            )}
        </div>
    )
}

// Получить CSS-классы задания количества колонок сетки объявлений под разные размеры экранов.
function getGridColumnsClassNames(
    config: AdsListGridColumnsConfig | number
): string {
    if (typeof config === 'number') {
        return `grid-columns-${config}`
    }
    const classNames: string[] = []
    // Игнорируем xxs
    classNames.push(`grid-columns-${config.xs}`)
    classNames.push(`grid-columns-sm-${config.sm}`)
    classNames.push(`grid-columns-md-${config.md}`)
    classNames.push(`grid-columns-lg-${config.lg}`)
    classNames.push(`grid-columns-xl-${config.xl}`)
    classNames.push(`grid-columns-xxl-${config.xxl}`)
    return classNames.join(' ')
}
